import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  PlayArrow as StepIcon,
  FastForward as RunToEndIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';
import ContextInspector from './ContextInspector';
import FormFieldRenderer from '../FormFieldRenderer';
import {
  getDebugContext,
  getFlowStatus,
  debugStep,
  debugRunToEnd,
  debugStop,
  debugModifyState,
  submitFormStreaming,
} from '../../api/agenticFlow.api';

export default function DebugPanel({
  executionId,
  onClose,
  onComplete,
  flowId,
  embedded = false, // When true, use normal layout (e.g. inside Dialog) instead of fixed position
  /** When true (e.g. Studio embed), hide the header close control — parent owns collapse. */
  hideChromeClose = false,
  streamEvents = [],
}) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepping, setStepping] = useState(false);
  const [error, setError] = useState(null);
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [modifyJson, setModifyJson] = useState('{}');
  const [resumeFormData, setResumeFormData] = useState({});
  const [submittingForm, setSubmittingForm] = useState(false);
  const [resumeInfo, setResumeInfo] = useState('');
  const [resumeToken, setResumeToken] = useState('');

  const fetchContext = useCallback(async (isInitial = false) => {
    if (!executionId) return;
    try {
      if (isInitial) setLoading(true);
      setError(null);
      let data;
      try {
        data = await getDebugContext(executionId);
      } catch (debugErr) {
        // Fallback: use flow-status when debug context fails (e.g. form pause, wrong URL)
        const statusData = await getFlowStatus(executionId);
        if (statusData?.success !== false) {
          const result = statusData?.result ?? statusData;
          data = {
            success: true,
            execution_id: executionId,
            status: statusData?.status ?? result?.status ?? 'paused',
            state: result?.variables ?? {},
            variables: statusData?.variables ?? result?.variables ?? {},
            execution_path: statusData?.execution_path ?? result?.execution_path ?? result?.execution_details?.execution_path ?? [],
            current_node: statusData?.current_node ?? result?.current_node ?? result?.current_form_message,
            form_fields: statusData?.form_fields ?? result?.form_fields ?? [],
          };
        } else {
          throw debugErr;
        }
      }
      setContext(data);
      // Don't auto-close on terminal status - let user inspect completed flow and close manually
    } catch (err) {
      setError(err?.message || 'Failed to load context');
      setContext(null);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [executionId, onComplete]);

  // Poll more frequently when waiting for first pause (pending/running)
  const status = context?.status || 'unknown';
  const isPaused = status === 'paused';
  const isWaitingForPause = status === 'pending' || status === 'running';
  const isTerminal = ['completed', 'success', 'complete', 'stopped'].includes(status);
  const pollInterval = isWaitingForPause ? 800 : 2000;
  const pausedFormFields = context?.form_fields || [];
  const showsPausedForm = isPaused && Array.isArray(pausedFormFields) && pausedFormFields.length > 0;
  const langGraphV2Events = Array.isArray(streamEvents)
    ? streamEvents.filter((ev) => ev?.langgraph_v2 || ev?.event_type)
    : [];

  useEffect(() => {
    fetchContext(true); // Initial load with loading spinner
    if (isTerminal) return; // Stop polling when flow is done
    const interval = setInterval(() => fetchContext(false), pollInterval);
    return () => clearInterval(interval);
  }, [fetchContext, pollInterval, isTerminal]);

  useEffect(() => {
    if (!showsPausedForm) return;
    setResumeFormData((prev) => {
      const next = { ...prev };
      pausedFormFields.forEach((f, idx) => {
        const key = f?.id || f?.name || `field_${idx}`;
        if (next[key] == null && f?.value != null) next[key] = f.value;
      });
      return next;
    });
  }, [showsPausedForm, pausedFormFields]);

  // F10 = Step Over
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F10' && isPaused && !stepping) {
        e.preventDefault();
        handleStep();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPaused, stepping]); // eslint-disable-line react-hooks/exhaustive-deps -- handleStep is stable

  const handleStep = async () => {
    try {
      setStepping(true);
      setError(null);
      const data = await debugStep(executionId);
      setContext((c) => ({ ...c, ...data }));
      await fetchContext();
      if (data.status === 'completed' && onComplete) {
        onComplete(data);
      }
    } catch (err) {
      setError(err?.message || 'Step failed');
    } finally {
      setStepping(false);
    }
  };

  const handleRunToEnd = async () => {
    try {
      setStepping(true);
      setError(null);
      const data = await debugRunToEnd(executionId);
      setContext((c) => ({ ...c, ...data }));
      if (onComplete) onComplete(data);
    } catch (err) {
      setError(err?.message || 'Run to end failed');
    } finally {
      setStepping(false);
    }
  };

  const handleStop = async () => {
    try {
      setStepping(true);
      setError(null);
      const data = await debugStop(executionId);
      setContext((c) => ({
        ...(c || {}),
        status: 'stopped',
        drain_requested: data?.graceful_drain ?? data?.drain_requested,
      }));
      if (data?.graceful_drain) {
        setResumeInfo('Graceful drain requested — run will stop after the current node.');
      } else {
        setResumeInfo('Debug run marked stopped.');
      }
      await fetchContext(false);
    } catch (err) {
      setError(err?.message || 'Stop failed');
    } finally {
      setStepping(false);
    }
  };

  const handleOpenModify = () => {
    setModifyJson(JSON.stringify(context?.variables || {}, null, 2));
    setModifyDialogOpen(true);
  };

  const handleModifyState = async () => {
    try {
      const parsed = JSON.parse(modifyJson);
      if (typeof parsed !== 'object' || parsed === null) {
        setError('Invalid JSON: must be an object');
        return;
      }
      setStepping(true);
      setError(null);
      const data = await debugModifyState(executionId, { variables: parsed });
      setModifyDialogOpen(false);
      setContext((c) => ({ ...c, ...data }));
      await fetchContext();
      if (data.status === 'completed' && onComplete) onComplete(data);
    } catch (err) {
      setError(err?.message || 'Modify state failed');
    } finally {
      setStepping(false);
    }
  };

  const handleSubmitPausedForm = async () => {
    if (!executionId || !showsPausedForm) return;
    try {
      setSubmittingForm(true);
      setError(null);
      setResumeInfo('');
      await submitFormStreaming(
        executionId,
        {
          ...resumeFormData,
          ...(resumeToken.trim() ? { resume_token: resumeToken.trim() } : {}),
        },
        (update) => {
          if (update?.type) setResumeInfo(`Resume update: ${update.type}`);
          if (update?.status && ['completed', 'success', 'complete'].includes(update.status) && onComplete) {
            onComplete(update);
          }
        },
        (err) => {
          setError(err?.message || 'Failed to resume paused form');
        },
      );
      await fetchContext(false);
      setResumeInfo((prev) => prev || 'Resume submitted.');
    } catch (err) {
      setError(err?.message || 'Failed to submit paused form');
    } finally {
      setSubmittingForm(false);
    }
  };

  return (
    <Paper
      elevation={embedded ? 0 : 6}
      sx={{
        ...(embedded ? {} : { position: 'fixed', right: 16, top: 80, width: 440, zIndex: 1300 }),
        maxHeight: embedded ? 'none' : '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">Debug Panel</Typography>
          {isPaused && <Chip label="PAUSED" size="small" color="warning" sx={{ fontWeight: 'bold' }} />}
          {isWaitingForPause && <Chip label="Waiting for pause..." size="small" color="default" />}
          {(status === 'completed' || status === 'success' || status === 'complete') && <Chip label="Completed" size="small" color="success" />}
          {status === 'stopped' && <Chip label="Stopped" size="small" color="error" />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Refresh context">
            <IconButton size="small" onClick={() => fetchContext(false)} sx={{ color: 'inherit' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!hideChromeClose && onClose && (
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title="Step Over (F10)">
          <Button
            size="small"
            variant="contained"
            startIcon={stepping ? <CircularProgress size={16} color="inherit" /> : <StepIcon />}
            onClick={handleStep}
            disabled={!isPaused || stepping}
          >
            Step
          </Button>
        </Tooltip>
        <Tooltip title="Run to End">
          <Button
            size="small"
            variant="outlined"
            startIcon={<RunToEndIcon />}
            onClick={handleRunToEnd}
            disabled={!isPaused || stepping}
          >
            Run to End
          </Button>
        </Tooltip>
        <Tooltip title="Stop">
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<StopIcon />}
            onClick={handleStop}
            disabled={stepping}
          >
            Stop
          </Button>
        </Tooltip>
        <Tooltip title="Modify state then step (time travel)">
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenModify}
            disabled={!isPaused || stepping}
          >
            Modify
          </Button>
        </Tooltip>
      </Box>

      <Dialog open={modifyDialogOpen} onClose={() => setModifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modify State (Time Travel)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Edit variables below. These will be merged into the current state before stepping.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={16}
            value={modifyJson}
            onChange={(e) => setModifyJson(e.target.value)}
            placeholder='{"input": "new value", "key": "value"}'
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleModifyState} variant="contained" disabled={stepping}>
            {stepping ? 'Applying...' : 'Apply & Step'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}
      {!!resumeInfo && (
        <Alert severity="info" sx={{ m: 1 }}>
          {resumeInfo}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', p: 1, minHeight: 200 }}>
        {showsPausedForm && (
          <Paper variant="outlined" sx={{ p: 1.25, mb: 1.25 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Waiting for human input
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {pausedFormFields.map((f, idx) => {
                const key = f?.id || f?.name || `field_${idx}`;
                return (
                  <FormFieldRenderer
                    key={key}
                    type={f?.type || 'text'}
                    label={f?.label || f?.name || key}
                    value={resumeFormData[key] ?? f?.value ?? ''}
                    placeholder={f?.placeholder || ''}
                    options={Array.isArray(f?.options) ? f.options : []}
                    required={Boolean(f?.required)}
                    onChange={(event) => {
                      const nextValue = event?.target?.value;
                      setResumeFormData((prev) => ({ ...prev, [key]: nextValue }));
                    }}
                  />
                );
              })}
              <TextField
                size="small"
                label="Resume token (optional)"
                value={resumeToken}
                onChange={(e) => setResumeToken(e.target.value)}
                helperText="Use when backend requires explicit HUMAN/WAIT resume token."
              />
              <Button
                size="small"
                variant="contained"
                onClick={handleSubmitPausedForm}
                disabled={submittingForm}
              >
                {submittingForm ? 'Submitting...' : 'Submit and resume'}
              </Button>
            </Box>
          </Paper>
        )}
        {loading && !context ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {langGraphV2Events.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                  <Typography variant="caption" fontWeight={700}>LangGraph v2 stream</Typography>
                  <Chip size="small" color="success" label={`${langGraphV2Events.length} events`} />
                  <Chip size="small" variant="outlined" label="typed metadata" />
                </Box>
                <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                  {langGraphV2Events.slice(-12).map((ev, i) => {
                    const meta = ev.langgraph_v2 || {};
                    return (
                      <Box key={`${meta.event || ev.event_type || ev.type}-${i}`} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" label={meta.event || ev.event_type || ev.type || 'event'} />
                        {(meta.name || ev.node_id) && <Chip size="small" variant="outlined" label={meta.name || ev.node_id} />}
                        {meta.run_id && <Chip size="small" variant="outlined" label={`run ${String(meta.run_id).slice(0, 8)}`} />}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}
            <ContextInspector
              state={context?.state || {}}
              variables={context?.variables || {}}
              executionPath={context?.execution_path || []}
              currentNode={context?.current_node}
              streamEvents={langGraphV2Events}
              nodeOutputs={context?.node_outputs || {}}
            />
          </>
        )}
      </Box>
    </Paper>
  );
}
